import angular from 'angular';
import _ from 'lodash';
import moment from 'moment-timezone';
import { currencies } from 'src/app/core/constants/currencies';
import { uiLanguages } from 'src/app/core/constants/ui-languages';
import { icons } from 'src/app/icons/icons';
import { EnvironmentConfig } from 'src/environments/environment-config';

const appModules = [
  'uiCropper',
  'ui.router',
  'ngMaterial',
  'ngSanitize',
  'pascalprecht.translate',
  'ngMessages',
  'hmTouchEvents',
  'core',
  'application',
  'tables',
  'printers',
  'printersNew',
  'cashregister',
  'items',
  'itemsNew',
  'history',
  'customers',
  'digitalPayments',
  'settings',
  'stock',
  'fidelity',
  'fidelityNew',
  'prepaid',
  'giftcard',
  'dashboard',
  'bookings',
  'kiosk',
  'promotions',
  'promotionsNew',
  'fileImporter',
  'suppliersNew',
  'settingsNew',
  'fileImporter',
  'fileExporter',
  'stockNew'
];

angular.module('TilbyApp', appModules)
    .run(tilbyBootstrap)
    .config(tilbyConfig);

tilbyConfig.$inject = ["$urlRouterProvider", "$locationProvider", "$translateProvider", "$compileProvider", "$mdInkRippleProvider", "$mdDateLocaleProvider", "$mdAriaProvider", "$mdThemingProvider", "$mdIconProvider"];

function tilbyConfig($urlRouterProvider, $locationProvider, $translateProvider, $compileProvider, $mdInkRippleProvider, $mdDateLocaleProvider, $mdAriaProvider, $mdThemingProvider, $mdIconProvider) {
    let localeUrlPrefix = '/';
    let html5Mode = true;

    switch(EnvironmentConfig.clientId) {
        case 'Scloby3PG':
            localeUrlPrefix = document.URL.substring(0, document.URL.lastIndexOf("/")).replace(/.*?:\/\//g, "") + '/';
            if(window.device.manufacturer == 'Apple') {
                html5Mode = false;
            }
            break;
        case 'SclobyElectron3':
            localeUrlPrefix = './';
            break;
        default: break;
    }

    $locationProvider.html5Mode({
        enabled: html5Mode,
        requireBase: true
    });

    $mdDateLocaleProvider.formatDate = function(date) {
        var m = moment(date);
        var finalDate = m.isValid() ? m.add(m.utcOffset(),'m').format('L') : '';
        return finalDate;
    };

    $mdDateLocaleProvider.parseDate = function(dateString) {
        var m = moment(dateString, 'L', true);
        return m.toDate();
    };

    let languageKeys = [];
    let languageMap = {};

    const browserLanguage = navigator.language.substring(0, 2);
    let preferredLanguage = 'it';

    for(const languageId in uiLanguages) {
        languageKeys.push(languageId);

        for(const territory of uiLanguages[languageId].territories) {
            languageMap[territory] = languageId;
        }

        if(languageId.substring(0, 2) == browserLanguage) {
            preferredLanguage = languageId;
        }
    }

    if(EnvironmentConfig.enableLanguageTestMode) {
        languageKeys.push('xx');
    }

    $translateProvider
        .useLoader('$translatePartialLoader', {
            urlTemplate: localeUrlPrefix + 'l10n/{lang}/{part}.json'
        })
        .registerAvailableLanguageKeys(languageKeys, languageMap)
        .addInterpolation('$translateMessageFormatInterpolation')
        .uniformLanguageTag('java');

    if(!EnvironmentConfig.enableLanguageTestMode) {
        $translateProvider
            .fallbackLanguage('it')
            .preferredLanguage(preferredLanguage);
    }

    /* Add New States Above */
    $urlRouterProvider.otherwise('/');

    $compileProvider.imgSrcSanitizationWhitelist(/^\s*((https?|ftp|file|blob|chrome-extension):|data:image\/)/);
    $compileProvider.aHrefSanitizationWhitelist(/^\s*(https?|ftp|mailto|tel|file|blob|chrome-extension):/);

    if(!['unstable', 'local'].includes(EnvironmentConfig.environment)) {
        $compileProvider.debugInfoEnabled(false);
    }

    $mdInkRippleProvider.disableInkRipple();
    $mdAriaProvider.disableWarnings();

    $mdThemingProvider.theme('default')
        .primaryPalette('blue')
        .accentPalette('blue');

    //Add custom icons
    for(const [icon, path] of Object.entries(icons)) {
        $mdIconProvider.icon(icon, path);
    }
}

tilbyBootstrap.$inject = ["$rootScope", "$state", "$transitions", "$window", "appToApp", "errorsLogger", "environmentInfo", "legacyBroadcaster", "appLoader", "sectionLoader", "startupWatchdog"];

function tilbyBootstrap($rootScope, $state, $transitions, $window, appToApp, errorsLogger, environmentInfo, legacyBroadcaster, appLoader, sectionLoader, startupWatchdog) {
    legacyBroadcaster.init();

    // The section loaders (a spinner over a part of a screen) are switched with this broadcast from the AngularJS code
    $rootScope.$on('loader:changeStatus', function(event, name, options) {
        if (options && options.enabled !== undefined) {
            sectionLoader.set(name, options.enabled);
        }
    });

    $rootScope.currentCurrency = _.cloneDeep(currencies.EUR);

    startupWatchdog.start();

    errorsLogger.debug('app.js run');

    if(environmentInfo.isMobileApp()) { //force InAppBrowser usage if running in cordova
        $window.open = $window.cordova.InAppBrowser.open;
    }

    if(environmentInfo.isElectronApp()) { //Destroy for second display if exists (used then restarting the application)
        $window.require('electron').ipcRenderer.invoke('close-customer-display');
    }

    $transitions.onStart({ }, function(transition) {
        errorsLogger.debug('Switching to state ' + transition.to().name);

        var fromModule = transition.$from().name.split('.')[1];
        var toModule = transition.$to().name.split('.')[1];

        if (fromModule !== toModule) {
            appLoader.show();
        }
    });

    $transitions.onEnter({ }, function(transition, state) {
        errorsLogger.debug('Entering state ' + state.name);
    });

    $transitions.onSuccess({ }, function(transition) {
        errorsLogger.debug('Reached state ' + transition.to().name);
        appLoader.hide();
    });

    $transitions.onError({ }, function(transition) {
        errorsLogger.debug('Error loading state ' + transition.to().name + ": " + transition.error());
        appLoader.hide();
        var errDetail = _.get(transition.error(), 'detail');

        if(_.startsWith(errDetail, 'SCL_')) {
            switch(errDetail) {
                case 'SCL_UPDATE_REQUIRED':
                    $state.go('update-required');
                break;
                case 'SCL_DEACTIVATED':
                    $state.go('deactivated');
                break;
                default:
                    $state.go('error', {
                        errorCode: errDetail
                    });
                break;
            }
        }
    });

    appToApp.init();
}

angular.module('TilbyApp').factory('$exceptionHandler', [function exceptionHandler () {
	return function (exception, cause) {
		try {
			if(exception.line && exception.column) {
				exception.message += ` ${exception.line}:${exception.column}`;
			}
		} catch(err) {
			//Nothing to do
		} finally {
			console.error.apply(console.error, arguments);
		}
	};
}]);